import { WebPlugin } from '@capacitor/core';
import { AppleSocialLogin } from './apple-provider';
import { inferUserCancelledError } from './errors';
import { FacebookSocialLogin } from './facebook-provider';
import { GoogleSocialLogin } from './google-provider';
import { asLinkedInLoginResult, buildLinkedInLoginOptions, buildLinkedInOAuthConfig, consumeLinkedInRedirectPending, isLinkedInOAuthResult, LINKEDIN_PROVIDER_ID, markLinkedInRedirectPending, } from './linkedin-provider';
import { clearOAuthPopupMarker, deliverOAuthResult, OAUTH_STATE_KEY, shouldAutoFinishOAuthRedirect, } from './oauth-popup-bridge';
import { OAuth2SocialLogin } from './oauth2-provider';
import { TelegramSocialLogin } from './telegram-provider';
import { asTikTokLoginResult, buildTikTokLoginOptions, buildTikTokOAuthConfig, isTikTokOAuthResult, TIKTOK_PROVIDER_ID, } from './tiktok-provider';
import { TwitterSocialLogin } from './twitter-provider';
export class SocialLoginWeb extends WebPlugin {
    constructor() {
        super();
        this.googleProvider = new GoogleSocialLogin();
        this.appleProvider = new AppleSocialLogin();
        this.facebookProvider = new FacebookSocialLogin();
        this.twitterProvider = new TwitterSocialLogin();
        this.telegramProvider = new TelegramSocialLogin();
        this.oauth2Provider = new OAuth2SocialLogin();
        // Auto-finish OAuth redirects only when running inside a popup window.
        // For redirect-based flows (full page navigation), the app should call `handleRedirectCallback()` explicitly.
        // Note: oauth-popup-redirect.ts also handles this eagerly on package import for COOP-safe popup completion.
        if (shouldAutoFinishOAuthRedirect()) {
            this.finishOAuthRedirectInPopup().catch((error) => {
                console.error('Failed to finish OAuth redirect', error);
                try {
                    window.close();
                }
                catch (_a) {
                    // ignore
                }
            });
        }
    }
    async parseRedirectResult() {
        var _a;
        const url = new URL(window.location.href);
        const stateRaw = localStorage.getItem(SocialLoginWeb.OAUTH_STATE_KEY);
        let provider = null;
        let state;
        let nonce;
        if (stateRaw) {
            try {
                const parsed = JSON.parse(stateRaw);
                provider = (_a = parsed.provider) !== null && _a !== void 0 ? _a : null;
                state = parsed.state;
                nonce = parsed.nonce;
            }
            catch (_b) {
                provider = stateRaw === 'true' ? 'google' : null;
            }
        }
        let result = null;
        switch (provider) {
            case 'twitter':
                result = await this.twitterProvider.handleOAuthRedirect(url, state);
                break;
            case 'telegram':
                result = await this.telegramProvider.handleOAuthRedirect(url, state);
                break;
            case 'oauth2':
                result = await this.oauth2Provider.handleOAuthRedirect(url, state);
                break;
            case 'google':
            default:
                result = this.googleProvider.handleOAuthRedirect(url);
                break;
        }
        return { provider, state, nonce, result };
    }
    async finishOAuthRedirectInPopup() {
        var _a, _b, _c;
        const parsed = await this.parseRedirectResult();
        const nonceOrState = (_a = parsed.nonce) !== null && _a !== void 0 ? _a : parsed.state;
        let message;
        if (!parsed.result) {
            message = {
                type: 'oauth-error',
                provider: parsed.provider,
                error: 'OAuth redirect did not contain expected parameters.',
            };
        }
        else if ('error' in parsed.result) {
            const resolvedProvider = (_b = parsed.provider) !== null && _b !== void 0 ? _b : null;
            const error = inferUserCancelledError(parsed.result.error);
            message = Object.assign({ type: 'oauth-error', provider: resolvedProvider, error: error.message }, (error.code ? { code: error.code } : null));
        }
        else {
            message = Object.assign({ type: 'oauth-response', provider: parsed.result.provider }, parsed.result.result);
        }
        deliverOAuthResult((_c = parsed.provider) !== null && _c !== void 0 ? _c : 'google', nonceOrState, message);
        clearOAuthPopupMarker();
        try {
            window.close();
        }
        catch (_d) {
            // Popup may not be allowed to close itself in some contexts
        }
    }
    async initialize(options) {
        var _a, _b, _c, _d, _e, _f;
        const initPromises = [];
        if ((_a = options.google) === null || _a === void 0 ? void 0 : _a.webClientId) {
            initPromises.push(this.googleProvider.initialize(options.google.webClientId, options.google.mode, options.google.hostedDomain, options.google.redirectUrl));
        }
        if ((_b = options.apple) === null || _b === void 0 ? void 0 : _b.clientId) {
            initPromises.push(this.appleProvider.initialize(options.apple.clientId, options.apple.redirectUrl, options.apple.useProperTokenExchange));
        }
        if ((_c = options.facebook) === null || _c === void 0 ? void 0 : _c.appId) {
            initPromises.push(this.facebookProvider.initialize(options.facebook.appId, options.facebook.locale));
        }
        if ((_d = options.twitter) === null || _d === void 0 ? void 0 : _d.clientId) {
            initPromises.push(this.twitterProvider.initialize(options.twitter.clientId, options.twitter.redirectUrl, options.twitter.defaultScopes, options.twitter.forceLogin, options.twitter.audience));
        }
        if ((_e = options.telegram) === null || _e === void 0 ? void 0 : _e.botId) {
            this.telegramProvider.initialize(options.telegram.botId, options.telegram.requestAccess, options.telegram.redirectUrl, options.telegram.origin, options.telegram.languageCode);
        }
        const oauth2Configs = Object.assign({}, ((_f = options.oauth2) !== null && _f !== void 0 ? _f : {}));
        if (options.linkedin && !oauth2Configs[LINKEDIN_PROVIDER_ID]) {
            oauth2Configs[LINKEDIN_PROVIDER_ID] = buildLinkedInOAuthConfig(options.linkedin);
        }
        if (options.tiktok) {
            if (!options.tiktok.clientKey || !options.tiktok.redirectUrl) {
                throw new Error('tiktok.clientKey and tiktok.redirectUrl are required');
            }
            if (!oauth2Configs[TIKTOK_PROVIDER_ID]) {
                oauth2Configs[TIKTOK_PROVIDER_ID] = buildTikTokOAuthConfig(options.tiktok);
            }
        }
        if (Object.keys(oauth2Configs).length > 0) {
            initPromises.push(this.oauth2Provider.initializeProviders(oauth2Configs));
        }
        await Promise.all(initPromises);
    }
    async login(options) {
        var _a;
        switch (options.provider) {
            case 'google':
                return this.googleProvider.login(options.options);
            case 'apple':
                return this.appleProvider.login(options.options);
            case 'facebook':
                return this.facebookProvider.login(options.options);
            case 'twitter':
                return this.twitterProvider.login(options.options);
            case 'telegram':
                return this.telegramProvider.login(options.options);
            case 'linkedin': {
                const linkedInOptions = (_a = options.options) !== null && _a !== void 0 ? _a : {};
                const oauthOptions = buildLinkedInLoginOptions(linkedInOptions);
                if (oauthOptions.flow === 'redirect' && oauthOptions.state) {
                    markLinkedInRedirectPending(oauthOptions.state);
                }
                const response = await this.oauth2Provider.login(oauthOptions);
                return asLinkedInLoginResult(response);
            }
            case 'tiktok': {
                const response = await this.oauth2Provider.login(buildTikTokLoginOptions(options.options));
                return asTikTokLoginResult(response);
            }
            case 'oauth2':
                return this.oauth2Provider.login(options.options);
            default:
                throw new Error(`Login for ${options.provider} is not implemented on web`);
        }
    }
    async logout(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.logout();
            case 'apple':
                return this.appleProvider.logout();
            case 'facebook':
                return this.facebookProvider.logout();
            case 'twitter':
                return this.twitterProvider.logout();
            case 'telegram':
                return this.telegramProvider.logout();
            case 'linkedin':
                return this.oauth2Provider.logout(LINKEDIN_PROVIDER_ID);
            case 'tiktok':
                return this.oauth2Provider.logout(TIKTOK_PROVIDER_ID);
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 logout');
                }
                return this.oauth2Provider.logout(options.providerId);
            default:
                throw new Error(`Logout for ${options.provider} is not implemented`);
        }
    }
    async isLoggedIn(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.isLoggedIn();
            case 'apple':
                return this.appleProvider.isLoggedIn();
            case 'facebook':
                return this.facebookProvider.isLoggedIn();
            case 'twitter':
                return this.twitterProvider.isLoggedIn();
            case 'telegram':
                return this.telegramProvider.isLoggedIn();
            case 'linkedin':
                return this.oauth2Provider.isLoggedIn(LINKEDIN_PROVIDER_ID);
            case 'tiktok':
                return this.oauth2Provider.isLoggedIn(TIKTOK_PROVIDER_ID);
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 isLoggedIn');
                }
                return this.oauth2Provider.isLoggedIn(options.providerId);
            default:
                throw new Error(`isLoggedIn for ${options.provider} is not implemented`);
        }
    }
    async getAuthorizationCode(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.getAuthorizationCode();
            case 'apple':
                return this.appleProvider.getAuthorizationCode();
            case 'facebook':
                return this.facebookProvider.getAuthorizationCode();
            case 'twitter':
                return this.twitterProvider.getAuthorizationCode();
            case 'linkedin':
                return this.oauth2Provider.getAuthorizationCode(LINKEDIN_PROVIDER_ID);
            case 'tiktok':
                return this.oauth2Provider.getAuthorizationCode(TIKTOK_PROVIDER_ID);
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 getAuthorizationCode');
                }
                return this.oauth2Provider.getAuthorizationCode(options.providerId);
            default:
                throw new Error(`getAuthorizationCode for ${options.provider} is not implemented`);
        }
    }
    async refresh(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.refresh();
            case 'apple':
                return this.appleProvider.refresh();
            case 'facebook':
                return this.facebookProvider.refresh(options.options);
            case 'twitter':
                return this.twitterProvider.refresh();
            case 'telegram':
                return this.telegramProvider.refresh();
            case 'linkedin':
                return this.oauth2Provider.refresh(LINKEDIN_PROVIDER_ID);
            case 'tiktok':
                return this.oauth2Provider.refresh(TIKTOK_PROVIDER_ID);
            case 'oauth2': {
                const oauth2Options = options.options;
                if (!(oauth2Options === null || oauth2Options === void 0 ? void 0 : oauth2Options.providerId)) {
                    throw new Error('providerId is required for oauth2 refresh');
                }
                return this.oauth2Provider.refresh(oauth2Options.providerId);
            }
            default:
                throw new Error(`Refresh for ${options.provider} is not implemented`);
        }
    }
    async providerSpecificCall(options) {
        var _a;
        switch (options.call) {
            case 'facebook#getProfile': {
                const fields = (_a = options.options) === null || _a === void 0 ? void 0 : _a.fields;
                return this.facebookProvider.getProfile(fields);
            }
            case 'facebook#requestTracking':
                return this.facebookProvider.requestTracking();
            case 'google#createRestoreCredential':
            case 'google#getRestoreCredential':
            case 'google#clearRestoreCredential':
                throw new Error(`${options.call} is only available on Android`);
            default:
                throw new Error(`Provider specific call for ${options.call} is not implemented`);
        }
    }
    async refreshToken(options) {
        if (options.provider === 'linkedin') {
            return this.oauth2Provider.refreshToken(LINKEDIN_PROVIDER_ID, options.refreshToken, options.additionalParameters);
        }
        if (options.provider === 'tiktok') {
            return this.oauth2Provider.refreshToken(TIKTOK_PROVIDER_ID, options.refreshToken, options.additionalParameters);
        }
        if (options.provider !== 'oauth2') {
            throw new Error('refreshToken is only implemented for oauth2, linkedin, and tiktok on web');
        }
        return this.oauth2Provider.refreshToken(options.providerId, options.refreshToken, options.additionalParameters);
    }
    async handleRedirectCallback() {
        var _a;
        const parsed = await this.parseRedirectResult();
        const callbackState = (_a = parsed.state) !== null && _a !== void 0 ? _a : new URL(window.location.href).searchParams.get('state');
        const pendingLinkedInRedirect = consumeLinkedInRedirectPending(callbackState);
        const result = parsed.result;
        if (!result)
            return null;
        if ('error' in result) {
            throw inferUserCancelledError(result.error);
        }
        if (pendingLinkedInRedirect && isLinkedInOAuthResult(result)) {
            return asLinkedInLoginResult(result);
        }
        if (isTikTokOAuthResult(result)) {
            return asTikTokLoginResult(result);
        }
        return result;
    }
    async decodeIdToken(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.idToken) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.token;
        if (!token) {
            throw new Error('idToken (or token) is required');
        }
        const claims = this.oauth2Provider.decodeIdToken(token);
        return { claims };
    }
    async getAccessTokenExpirationDate(options) {
        if (typeof (options === null || options === void 0 ? void 0 : options.accessTokenExpirationDate) !== 'number') {
            throw new Error('accessTokenExpirationDate is required');
        }
        return { date: new Date(options.accessTokenExpirationDate).toISOString() };
    }
    async isAccessTokenAvailable(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.accessToken) !== null && _a !== void 0 ? _a : null;
        return { isAvailable: typeof token === 'string' && token.length > 0 };
    }
    async isAccessTokenExpired(options) {
        if (typeof (options === null || options === void 0 ? void 0 : options.accessTokenExpirationDate) !== 'number') {
            throw new Error('accessTokenExpirationDate is required');
        }
        return { isExpired: options.accessTokenExpirationDate <= Date.now() };
    }
    async isRefreshTokenAvailable(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.refreshToken) !== null && _a !== void 0 ? _a : null;
        return { isAvailable: typeof token === 'string' && token.length > 0 };
    }
    async getPluginVersion() {
        return { version: 'web' };
    }
    async openSecureWindow(options) {
        const w = 600;
        const h = 550;
        const settings = [
            ['width', w],
            ['height', h],
            ['left', screen.width / 2 - w / 2],
            ['top', screen.height / 2 - h / 2],
        ]
            .map((x) => x.join('='))
            .join(',');
        const popup = window.open(options.authEndpoint, 'Authorization', settings);
        if (typeof popup.focus === 'function') {
            popup.focus();
        }
        return new Promise((resolve, reject) => {
            const bc = new BroadcastChannel(options.broadcastChannelName || 'oauth-channel');
            bc.addEventListener('message', (event) => {
                if (event.data.startsWith(options.redirectUri)) {
                    bc.close();
                    resolve({ redirectedUri: event.data });
                }
                else {
                    bc.close();
                    reject(new Error('Redirect URI does not match, expected ' + options.redirectUri + ' but got ' + event.data));
                }
            });
            setTimeout(() => {
                bc.close();
                reject(new Error('The sign-in flow timed out'));
            }, 5 * 60000);
        });
    }
}
SocialLoginWeb.OAUTH_STATE_KEY = OAUTH_STATE_KEY;
//# sourceMappingURL=web.js.map